import React from 'react';
import PropTypes from 'prop-types';
import Selection from './../../../selection/Selection';

import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { buildSpectateUrl } from '~spa/Utils/Spectate';

import { get } from '~shared/utils/http';
import { nextRepaint } from '~shared/utils/dom';
import {
    betslipHasSelection,
    addToBetslip,
    betLocateAdd,
    betLocatePop,
    bsNumber,
} from '~shared/utils/betslip';

import {
    SP_ONLY_CODE,
} from './../../../sport-event/constants';

import { FRACTION_ODDS_FORMAT, AMERICAN_ODDS_FORMAT } from '~shared/utils/constants';
import { GA_COMPONENT_CSS_ATTRIBUTE_NAME } from '~shared/utils/google-analytics';

import {
    BB_BETCARD_TICKS_ENDPOINT,
    BB_BETCARD_HIGHEST_ODD_VALUE,
    BB_BETCARD_HIGHEST_ODD_DECIMAL_VALUE,
    BB_BETCARD_HIGHEST_ODD_FRACTION_VALUE,
    BB_BETCARD_HIGHEST_ODD_AMERICAN_VALUE,
    BB_BETCARD_GA_COMPONENT,
    BB_BETCARD_GA_UNKNOWN_COMPONENT,
    GTM_MULTIPLE_SELECTION,
} from '../constants';

import { getOddsFormat } from '~shared/utils/user';

export default class MultiBetButton extends React.Component {

    static propTypes = {
        selections: PropTypes.array.isRequired,
        betcard: PropTypes.object.isRequired,
        forceSuspended: PropTypes.bool.isRequired,
        gtmAttribute: PropTypes.string,
    }

    static defaultProps = {
        gtmAttribute: '',
    }

    constructor(props) {
        super(props);
        this.state = isUndefined(this.state) && {};
        this.betButton = null;
        this.betButtonEl = null;
        this.toggleSelectionsToBetslip = this.toggleSelectionsToBetslip.bind(this);
        this.isFractionUnit = getOddsFormat() === FRACTION_ODDS_FORMAT;
        this.isAmericanUnit = getOddsFormat() === AMERICAN_ODDS_FORMAT;
    }

    resetBetButtonDisplayData() {
        const one = 1;
        this.decimalOdds = one;
        this.spOnly = false;
        this.suspended = false;
        this.fractionalOdds = '';
    }

    getAccumulatedOdds(selections) {
        const one = 1;

        const [decimalOdds, spOnly, suspended] = selections.reduce((acc, selection) => {
            const [odds, sp = false] = acc;

            return [
                odds * Number(selection.decimal_price),
                !this.spOnly && (sp || selection.sp_only || isNull(selection.decimal_price)),
                !this.suspended && selection.suspended,
            ];
        }, [one]);

        const fractionalOddsNumerator = decimalOdds - one;
        const fixedFractionalOddsNumerator = bsNumber(fractionalOddsNumerator);
        const fixedDecimalOdds = bsNumber(decimalOdds);
        const fractionalOdds = `${fixedFractionalOddsNumerator}/${one}`;


        if(spOnly){
            return [SP_ONLY_CODE, SP_ONLY_CODE, spOnly, suspended];
        }

        return [fixedDecimalOdds, fractionalOdds, spOnly, suspended];
    }

    calculateOddsFromSelections(selections) {
        this.resetBetButtonDisplayData();
        ([
            this.decimalOdds,
            this.fractionalOdds,
            this.spOnly,
            this.suspended,
        ] = this.getAccumulatedOdds(selections));
    }

    toggleSelectionsToBetslip() {
        if (this.props.forceSuspended) {
            return false;
        }

        this.props.selections.forEach(selection => {
            /**
             * We add one by one in case we hit the betslip limit (25 at time of writing)
             * add() will remove selections already in the betslip
             */
            addToBetslip([{ single: selection.id }], this.gaComponent);
            if (!betslipHasSelection(selection.id)) {
                betLocateAdd(String(selection.id), this.gaComponent);
            } else {
                betLocatePop(String(selection.id));
            }

        });

        return true;
    }

    componentDidMount() {
        this.calculateOddsFromSelections(this.props.selections);
        this.initialiseBetButton();
        const gaComponentEl = this.betButtonEl.closest(`[${BB_BETCARD_GA_COMPONENT}]`);
        this.gaComponent = !isNull(gaComponentEl) ? gaComponentEl.getAttribute(BB_BETCARD_GA_COMPONENT) : BB_BETCARD_GA_UNKNOWN_COMPONENT;
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(this.props) !== JSON.stringify(prevProps)) {
            this.calculateOddsFromSelections(this.props.selections);
            this.setOdds(this.props.forceSuspended);
        }
    }

    async getTicks(sportId) {
        const getBetcardsAjaxGetOptions = () => {
            return {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            };
        };
        let ticks = JSON.parse(sessionStorage.getItem('betfeed_ticks') || null);
        if (!ticks) {
            try {
                const response = await get(
                    buildSpectateUrl(`${BB_BETCARD_TICKS_ENDPOINT}?sport_id=${sportId}`), getBetcardsAjaxGetOptions(),
                );
                if (response.ok) {
                    ticks = await response.json();
                    if (ticks) {
                        sessionStorage.setItem('betfeed_ticks', JSON.stringify(ticks));
                    }
                }
            } catch (e) {
                ticks = {};
            }
        }

        return ticks || {};
    }

    async calculateFractionOdds(odds) {
        const ticks = await this.getTicks();
        const maxOdds = 1.5;
        let closest = { odds: undefined, diff: maxOdds };

        for (let tick of Object.keys(ticks)) {
            const diff = Math.abs(odds - Number(tick));
            if (!diff) {
                return ticks[tick];
            }
            closest = diff < closest.diff ? { odds: tick, diff } : closest;
        }

        return ticks[closest.odds];
    }

    setOdds(forceSuspended=false) {
        const nextState = {
            suspended: this.suspended || forceSuspended,
            decimal_price: this.decimalOdds,
            fraction_price: this.fractionalOdds,
            sp_only: this.spOnly,
        };
        nextRepaint(() => {
            this.betButton.update(nextState);
            this.setMaximumDisplayableOdds();
        });
    }

    setMaximumDisplayableOdds() {
        if (this.decimalOdds > BB_BETCARD_HIGHEST_ODD_VALUE) {
            if (this.isFractionUnit) {
                this.betButton.setOdds(BB_BETCARD_HIGHEST_ODD_FRACTION_VALUE);
            } else if (this.isAmericanUnit) {
                this.betButton.setOdds(BB_BETCARD_HIGHEST_ODD_AMERICAN_VALUE);
            } else {
                this.betButton.setOdds(BB_BETCARD_HIGHEST_ODD_DECIMAL_VALUE);
            }

        }
    }

    initialiseBetButton() {
        let selectionIds = [];
        this.props.selections.forEach((selection) => {
            selectionIds.push(selection.id);
        });
        const initialState = {
            id: selectionIds.join('_'),
            suspended: this.suspended,
            decimal_price: this.decimalOdds,
            fraction_price: this.fractionalOdds,
            sp_only: this.spOnly,
        };
        this.betButton = new Selection({
            initialState: initialState,
            gtmAttribute: `${this.props.gtmAttribute};${GTM_MULTIPLE_SELECTION};`,
        });

         // Capture the event to prevent it bubbling to the global Selection element listener
        this.betButton.el.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleSelectionsToBetslip();

            return false;
        }, true);

        const gaComponentEl = this.betButtonEl.closest(`[${GA_COMPONENT_CSS_ATTRIBUTE_NAME}]`);
        this.gaComponent = !isNull(gaComponentEl) ? gaComponentEl.getAttribute(GA_COMPONENT_CSS_ATTRIBUTE_NAME) : 'unknown_component';
        nextRepaint(() => {
            this.betButtonEl.appendChild(this.betButton.el);
            this.setMaximumDisplayableOdds();
        });
    }

    render() {
        return (
            <div
                ref={el => { this.betButtonEl = el; }}
                title={this.isFractionUnit ? this.fractionalOdds : this.decimalOdds}></div>
        );
    }
}
